import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Box,
  Button,
  Dialog,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "react-query";
import { useEffect, useState } from "react";
import {
  getArea,
  getBrand,
  getNeedsFilters,
} from "../../../../services/needs/needs_service";
import { getDistrictMasterData } from "../../../../services/master/master_service";
import { NeedFilters, Option } from "../../model/needs_filters";
import { useNeedFiltersStore } from "../../../../store/use-needs-filter-store";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

const statusList = [
  { id: 1, name: "Submitted" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "Closed" },
];

export default function NeedFiltersModel({
  open,
  onDialogClose,
  states = [],
  statesLoading = false,
}: {
  open: boolean;
  onDialogClose: () => void;
  states?: { id: number; value: string }[];
  statesLoading?: boolean;
}) {
  const {
    districtId,
    setDistrictId,
    stateId,
    setStateId,
    areasId,
    setAreasId,
    concerningToId,
    setConcerningToId,
    status,
    setStatus,
    cityId,
    setCityId,
    subcategoryId,
    setSubcategoryId,
    brandId,
    setBrandId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    resetFilters,
    setPaginationModel, paginationModel
  } = useNeedFiltersStore();

  const handleApplyFilter = () => {
    setPaginationModel({ ...paginationModel, page: 1 })
    onDialogClose();
  }

  const { data: needsFilters, isLoading: needsFiltersLoading } = useQuery<NeedFilters>(
    ["needs-filters"],
    async () => (await getNeedsFilters())?.data?.data,
    { enabled: open }
  );

  const { data: district, isFetching: districtLoading } = useQuery(
    ["districtList", stateId],
    async () =>
      stateId ? (await getDistrictMasterData(stateId))?.data?.data?.list : [],
    { enabled: open && !!stateId }
  );

  const [areas, setAreas] = useState<{ id: number; value: string }[]>([]);
  const [brands, setBrands] = useState<{ id: number; value: string }[]>([]);
  const [areasBrandsLoading, setAreasBrandsLoading] = useState(false);

  useEffect(() => {
    if (subcategoryId) {
      setAreasBrandsLoading(true);
      Promise.all([
        getArea({ categoryId: concerningToId, subcategoryId }),
        getBrand({ categoryId: concerningToId, subcategoryId }),
      ])
        .then(([areaRes, brandRes]) => {
          setAreas(areaRes?.data?.data?.list || []);
          setBrands(brandRes?.data?.data?.list || []);
        })
        .catch(() => {
          setAreas([]);
          setBrands([]);
        })
        .finally(() => setAreasBrandsLoading(false));
    } else {
      setAreas([]);
      setBrands([]);
    }
  }, [subcategoryId, concerningToId]);

  const filtersLoading = needsFiltersLoading || districtLoading || areasBrandsLoading || statesLoading;

  useEffect(() => {
    // Ensure end date is not before start date
    if (endDate && startDate && dayjs(startDate, "DD-MM-YYYY").isAfter(dayjs(endDate, "DD-MM-YYYY"))) {
      setEndDate('');
    }
  }, [startDate, endDate, setEndDate]);

  function getSubCategories(): Option[] {
    return (
      needsFilters?.categories?.find(
        (c) => c.category._id === concerningToId
      )?.subcategory || []
    );
  }

  const chipsOption = ({
    title,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    options: { id: number; name: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) => (
    <Box pt={2} pb={1}>
      <Typography variant="body2" fontWeight={600} color={black}>
        {title}
      </Typography>
      <Box pt={1} pb={1} display="flex" flexWrap="wrap" gap={1}>
        {options.map((option) => (
          <Chip
            key={option.id}
            variant={selectedId === option.id ? "filled" : "outlined"}
            color="primary"
            label={option.name}
            clickable
            onClick={() => onSelect(selectedId === option.id ? null : option.id)}
          />
        ))}
      </Box>
    </Box>
  );

  const dropdownOption = ({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
    disabled,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; name: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
    disabled?: boolean;
  }) => (
    <Box pt={2} pb={1}>
      <Typography variant="body2" fontWeight={600} color={black}>
        {title}
      </Typography>
      <Autocomplete
        disabled={disabled}
        fullWidth
        size="small"
        value={options.find((o) => selectedId === o.id) || null}
        onChange={(_, value) => onSelect(value?.id ?? null)}
        options={options}
        getOptionLabel={(o) => o.name}
        componentsProps={{ paper: popoverPaperSx }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: 2,
                "& fieldset": { borderColor: "#ABA0AB" },
                "&:hover fieldset": { borderColor: purple },
                "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
              },
            }}
          />
        )}
      />
    </Box>
  );

  const dateRange = () => (
    <Box pt={2} pb={1}>
      <Typography variant="body2" fontWeight={600} color={black}>
        Date Range
      </Typography>
      <Box pt={1} pb={1} display="flex" gap={1.5}>
        <DatePicker
          label="Start Date"
          format="DD-MM-YYYY"
          disableFuture
          value={startDate ? dayjs(startDate, "DD-MM-YYYY") : null}
          onChange={(v: Dayjs | null) => setStartDate(v?.format("DD-MM-YYYY") || '')}
          slotProps={{ desktopPaper: popoverPaperSx }}
          sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
        />
        <DatePicker
          label="End Date"
          format="DD-MM-YYYY"
          disableFuture
          minDate={startDate ? dayjs(startDate, "DD-MM-YYYY") : undefined}
          value={endDate ? dayjs(endDate, "DD-MM-YYYY") : null}
          onChange={(v: Dayjs | null) => setEndDate(v?.format("DD-MM-YYYY") || '')}
          slotProps={{ desktopPaper: popoverPaperSx }}
          sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onDialogClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 500, maxWidth: "90vw" }} display="flex" flexDirection="column">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Needs
          </Typography>
          <IconButton onClick={onDialogClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          {needsFiltersLoading || statesLoading ? (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={28} sx={{ color: purple }} />
            </Box>
          ) : (
            <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={resetFilters}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5 }} />

          {chipsOption({
            title: "Category",
            selectedId: concerningToId,
            options:
              needsFilters?.categories?.map((c) => ({
                id: c.category._id,
                name: c.category.name,
              })) || [],
            onSelect: (id) => {
              setConcerningToId(id);
              setSubcategoryId(null);
              setAreasId(null);
              setBrandId(null);
              setAreas([]);
              setBrands([]);
            },
          })}

          {concerningToId && getSubCategories().length > 0 &&
            chipsOption({
              title: concerningToId === 2 ? "Company" : "Sub Category",
              selectedId: subcategoryId,
              options: getSubCategories().map((s) => ({ id: s._id, name: s.name })),
              onSelect: setSubcategoryId,
            })}

          {dropdownOption({
            disabled: areasBrandsLoading || areas.length === 0,
            title: "Areas",
            placeholder: areasBrandsLoading ? "Loading..." : "Select Area",
            options: areas.map((a) => ({ id: a.id, name: a.value })),
            selectedId: areasId,
            onSelect: setAreasId,
          })}

          {concerningToId === 2 &&
            dropdownOption({
              disabled: areasBrandsLoading || brands.length === 0,
              title: "Brands",
              placeholder: areasBrandsLoading ? "Loading..." : "Select Brand",
              options: brands.map((b) => ({ id: b.id, name: b.value })),
              selectedId: brandId,
              onSelect: setBrandId,
            })}

          <Divider sx={{ mt: 1 }} />
          {dropdownOption({
            title: "State",
            placeholder: "Select State",
            options: (Array.isArray(states) ? states : []).map((s) => ({ id: s.id, name: s.value })),
            selectedId: stateId,
            onSelect: setStateId,
          })}

          {dropdownOption({
            disabled: districtLoading,
            title: "District",
            placeholder: districtLoading ? "Loading..." : "Search for District",
            options:
              district?.map((d: { id: number; value: string }) => ({
                id: d.id,
                name: d.value,
              })) || [],
            selectedId: districtId,
            onSelect: setDistrictId,
          })}

          {dropdownOption({
            title: "City",
            placeholder: "Select City",
            options:
              needsFilters?.cityList?.map((c) => ({ id: c._id, name: c.name })) ||
              [],
            selectedId: cityId,
            onSelect: setCityId,
          })}

          {chipsOption({
            title: "Status",
            selectedId: status,
            options: statusList,
            onSelect: setStatus,
          })}

          {dateRange()}
            </>
          )}
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" gap={1.5}>
            <Button
              fullWidth
              onClick={onDialogClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Close
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={filtersLoading}
              onClick={handleApplyFilter}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
